import { Injectable, signal } from '@angular/core';


export interface ShieldTemplate {
  id: string;
  name: string;
  available: boolean;
  defaultPrimaryColor: string;
  defaultSecondaryColor: string;
  defaultInitials: string;
  viewBox: string;
  svgTemplate: string;
  description?: string;
}

@Injectable({ providedIn: 'root' })
export class ShieldTemplateRegistry {
  private readonly templates = signal<ShieldTemplate[]>([]);
  private pending: Promise<void> | null = null;

  constructor() {
    void this.refreshTemplatesFromAssets();
  }

  refreshTemplatesFromAssets(): Promise<void> {
    if (this.pending) return this.pending;
    this.pending = this.loadAssets().finally(() => { this.pending = null; });
    return this.pending;
  }

  private async loadAssets(): Promise<void> {
    try {
      const options: RequestInit = { cache: 'no-store', headers: { 'ngsw-bypass': 'true' } };
      const response = await fetch('/assets/shields/index.json', options);
      if (!response.ok) throw new Error('No se pudo cargar el catálogo de escudos');
      const files: string[] = await response.json();
      const loaded = await Promise.all(files.map(async (file): Promise<ShieldTemplate | null> => {
        if (!/^[a-z0-9_-]+\.svg$/i.test(file)) return null;
        try {
          const res = await fetch('/assets/shields/' + encodeURIComponent(file), options);
          if (!res.ok) return null;
          const svg = await res.text();
          if (!/<svg[\s>]/i.test(svg)) return null;
          const id = file.replace(/(?:_template)?\.svg$/i, '').toUpperCase();
          return {
            id, name: id, available: true, defaultInitials: id.slice(0, 3),
            defaultPrimaryColor: '#123D83', defaultSecondaryColor: '#F5B800',
            viewBox: svg.match(/viewBox="([^"]+)"/)?.[1] || '0 0 240 280',
            svgTemplate: svg,
          };
        } catch { return null; }
      }));
      this.templates.set(loaded.filter((t): t is ShieldTemplate => t !== null));
    } catch {
      this.templates.set([]);
    }
  }

  getTemplate(id: string): ShieldTemplate | undefined {
    return this.templates().find(t => t.id === id);
  }

  getAllTemplates(): ShieldTemplate[] {
    return this.templates();
  }

  getAvailableTemplates(): ShieldTemplate[] {
    return this.templates().filter(t => t.available);
  }

  /**
   * Modifica los elementos vectoriales del SVG de la plantilla:
   * - Elementos con class="primary", .shield-primary o data-color="primary" -> fill=primaryColor
   * - Elementos con class="secondary", .shield-secondary o data-color="secondary" -> stroke/fill=secondaryColor
   * - Superpone las 3 iniciales con alta visibilidad
   */
  renderShieldSvg(
    templateId: string,
    primaryColor: string,
    secondaryColor: string,
    initials: string,
    textColor?: string
  ): string {
    const template = this.getTemplate(templateId);
    if (!template) return '';
    const safePrimary = this.sanitizeHexColor(primaryColor, template.defaultPrimaryColor);
    const safeSecondary = this.sanitizeHexColor(secondaryColor, template.defaultSecondaryColor);
    const safeTextColor = this.sanitizeHexColor(textColor, safePrimary);
    const safeInitials = this.sanitizeInitials(initials, template.defaultInitials);

    // Resolve the variables in both CSS rules and presentation attributes. SVG CSS
    // overrides fill/stroke attributes, so changing attributes alone leaves grey layers.
    let svg = template.svgTemplate
      .replace(/var\(\s*--shield-primary(?:\s*,[^)]*)?\)/gi, safePrimary)
      .replace(/var\(\s*--shield-secondary(?:\s*,[^)]*)?\)/gi, safeSecondary);

    // 1. Recolorear capa principal (primary)
    // - Si tiene fill (no "none"), reemplazar el color fill
    svg = svg.replace(
      /(<(?:path|rect|polygon|circle|ellipse|g)[^>]*(?:class="[^"]*primary[^"]*"|data-color="primary")[^>]*fill=")(?!none")[^"]*(")/gi,
      `$1${safePrimary}$2`
    );

    // 2. Recolorear capa secundaria (secondary)
    // - Reemplazar fill si no es "none"
    svg = svg.replace(
      /(<(?:path|rect|polygon|circle|ellipse|g)[^>]*(?:class="[^"]*secondary[^"]*"|data-color="secondary")[^>]*fill=")(?!none")[^"]*(")/gi,
      `$1${safeSecondary}$2`
    );
    // - Reemplazar stroke si es url(#ridge) o cualquier otro color para dar intensidad
    svg = svg.replace(
      /(<(?:path|rect|polygon|circle|ellipse|g)[^>]*(?:class="[^"]*secondary[^"]*"|data-color="secondary")[^>]*stroke=")[^"]*(")/gi,
      `$1${safeSecondary}$2`
    );

    // 3. Manejo de iniciales (3 letras)
    const textNodeRegex = /(<text[^>]*(?:data-initials="true"|class="[^"]*shield-initials[^"]*")[^>]*>)([^<]*)(<\/text>)/gi;
    
    if (textNodeRegex.test(svg)) {
      // Si ya contiene un elemento text para iniciales, actualizar o añadir su fill con textColor
      svg = svg.replace(
        /(<text[^>]*(?:data-initials="true"|class="[^"]*shield-initials[^"]*"))([^>]*>)/gi,
        (match, p1, p2) => {
          if (/fill="[^"]*"/i.test(p2)) {
            return `${p1}${p2.replace(/fill="[^"]*"/i, `fill="${safeTextColor}"`)}`;
          } else {
            return `${p1} fill="${safeTextColor}"${p2}`;
          }
        }
      );
      svg = svg.replace(textNodeRegex, `$1${safeInitials}$3`);
    } else {
      // Inyectar el nodo de texto de iniciales antes del cierre del último <g> o </svg>
      const initialsSvgNode = `
    <!-- Initials Overlay -->
    <text
      class="shield-initials"
      data-initials="true"
      x="120"
      y="142"
      text-anchor="middle"
      dominant-baseline="central"
      fill="${safeTextColor}"
      stroke="#000000"
      stroke-width="1"
      paint-order="stroke fill"
      font-family="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Montserrat', sans-serif"
      font-weight="900"
      font-size="34"
      letter-spacing="2.5"
      style="filter: drop-shadow(0px 2px 3px rgba(0,0,0,0.5));"
    >${safeInitials}</text>`;

      if (svg.includes('</g>')) {
        const lastGIndex = svg.lastIndexOf('</g>');
        svg = svg.slice(0, lastGIndex) + initialsSvgNode + svg.slice(lastGIndex);
      } else if (svg.includes('</svg>')) {
        svg = svg.replace('</svg>', `${initialsSvgNode}\n</svg>`);
      }
    }

    return svg;
  }

  sanitizeHexColor(color: string | undefined | null, fallback: string): string {
    if (!color) return fallback;
    const trimmed = color.trim();
    if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(trimmed)) {
      return trimmed.toUpperCase();
    }
    return fallback;
  }

  sanitizeInitials(initials: string | undefined | null, fallback: string): string {
    if (initials == null) return fallback.slice(0, 3).toUpperCase();
    const cleaned = initials.replace(/[^A-Za-z0-9]/g, '').slice(0, 3).toUpperCase();
    return cleaned;
  }
}

